import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import { BookOpen, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { booksApi, type Book } from "../lib/books-api";
import { EmptyState } from "@/components/empty-state";
import { IndexTable } from "@/components/index-table";
import { ErrorState } from "@/components/layout";
import { PageHeader } from "@/components/page-header";
import { StatusBadge } from "@/components/status-badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useUrlParam } from "@/hooks/use-url-state";

const PROVIDER_LABELS: Record<string, string> = {
  openai: "OpenAI",
  gemini: "Gemini",
};

export default function Books() {
  const navigate = useNavigate();
  const [books, setBooks] = useState<Book[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [query, setQuery] = useUrlParam("q", "");

  function reload() {
    setError(null);
    booksApi
      .list()
      .then((r) => setBooks(r.books))
      .catch((err) => setError((err as Error).message));
  }
  useEffect(reload, []);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!books || !q) return books ?? [];
    return books.filter((b) =>
      [b.title, b.slug, ...b.shopifyProductHandles].some((v) =>
        v.toLowerCase().includes(q),
      ),
    );
  }, [books, query]);

  const columns: ColumnDef<Book, unknown>[] = [
    {
      id: "title",
      header: "Buch",
      cell: ({ row }) => (
        <div className="leading-tight">
          <Link
            to={`/books/${row.original.id}`}
            className="font-semibold hover:underline focus-visible:underline"
          >
            {row.original.title}
          </Link>
          <div className="font-mono text-xs text-muted-foreground">
            {row.original.slug}
          </div>
        </div>
      ),
    },
    {
      id: "mapping",
      header: "Shopify-Mapping",
      cell: ({ row }) => {
        const handles = row.original.shopifyProductHandles;
        return handles.length === 0 ? (
          <StatusBadge
            tone="critical"
            title="Ohne Mapping lassen sich Bestellungen nicht zuordnen."
          >
            Kein Mapping
          </StatusBadge>
        ) : (
          <span
            title={handles.join("\n")}
            className="block max-w-72 truncate text-muted-foreground"
          >
            {handles.join(" · ")}
          </span>
        );
      },
    },
    {
      id: "preview",
      header: "Preview",
      cell: ({ row }) =>
        PROVIDER_LABELS[row.original.previewSettings?.provider] ??
        row.original.previewSettings?.provider ??
        "—",
    },
    {
      id: "scenes",
      header: "Szenen",
      cell: ({ row }) =>
        PROVIDER_LABELS[row.original.sceneSettings?.provider] ??
        row.original.sceneSettings?.provider ??
        "—",
    },
    {
      id: "updated",
      header: "Geändert",
      cell: ({ row }) => (
        <span className="text-muted-foreground">
          {new Date(row.original.updatedAt).toLocaleDateString("de-DE")}
        </span>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Bücher"
        actions={
          <Button onClick={() => setCreating(true)}>
            <Plus />
            Neues Buch
          </Button>
        }
      />
      {error && (
        <ErrorState className="mb-3" message={error} onRetry={reload} />
      )}

      <IndexTable
        search={{
          value: query,
          onChange: setQuery,
          placeholder: "Suchen: Titel, Slug, Shopify-Mapping",
          label: "Bücher durchsuchen",
        }}
        columns={columns}
        data={visible}
        getRowId={(b) => b.id}
        onRowClick={(b) => navigate(`/books/${b.id}`)}
        loading={!books && !error}
        empty={
          query.trim() ? (
            <EmptyState
              icon={Search}
              title="Keine Treffer"
              description={`Kein Buch passt zu „${query.trim()}“.`}
              action={
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setQuery("")}
                >
                  Suche zurücksetzen
                </Button>
              }
            />
          ) : (
            <EmptyState
              icon={BookOpen}
              title="Noch keine Bücher angelegt"
              action={
                <Button size="sm" onClick={() => setCreating(true)}>
                  <Plus />
                  Neues Buch
                </Button>
              }
            />
          )
        }
      />

      <CreateBookDialog
        open={creating}
        onOpenChange={setCreating}
        onCreated={(id) => {
          setCreating(false);
          toast.success("Buch angelegt");
          navigate(`/books/${id}`);
        }}
      />
    </div>
  );
}

// Gleiche Regel wie der Server (routes/books.ts).
const SLUG_RE = /^[a-z0-9][a-z0-9-]*$/;

function CreateBookDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (id: string) => void;
}) {
  const [slug, setSlug] = useState("");
  const [title, setTitle] = useState("");
  const [sceneCount, setSceneCount] = useState(16);
  const [handles, setHandles] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [touched, setTouched] = useState({ slug: false, title: false });

  // Beim Öffnen frisch starten.
  useEffect(() => {
    if (!open) return;
    setSlug("");
    setTitle("");
    setSceneCount(16);
    setHandles("");
    setErr(null);
    setTouched({ slug: false, title: false });
  }, [open]);

  const slugError = !slug.trim()
    ? "Bitte einen Slug angeben."
    : slug.length > 120
      ? "Höchstens 120 Zeichen."
      : !SLUG_RE.test(slug)
        ? "Nur Kleinbuchstaben, Ziffern und Bindestriche, z. B. kofi-und-das-bruellen."
        : null;
  const titleError = !title.trim() ? "Bitte einen Titel angeben." : null;
  const sceneError =
    !Number.isInteger(sceneCount) || sceneCount < 0
      ? "Bitte eine ganze Zahl ab 0 angeben."
      : null;
  const valid = !slugError && !titleError && !sceneError;

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setTouched({ slug: true, title: true });
    if (!valid) return;
    setBusy(true);
    setErr(null);
    try {
      const lines = handles
        .split("\n")
        .map((l) => l.trim())
        .filter((l) => l.length > 0);
      const r = await booksApi.create({
        slug,
        title,
        scene_count: sceneCount,
        shopify_product_handles: lines,
      });
      onCreated(r.book.id);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <form onSubmit={save} noValidate className="grid gap-4">
          <DialogHeader>
            <DialogTitle>Neues Buch anlegen</DialogTitle>
            <DialogDescription>
              Alles Weitere (Cover, Szenen, Druck-Titel) richtest du danach im
              Buch ein.
            </DialogDescription>
          </DialogHeader>

          <FieldGroup>
            <Field data-invalid={touched.title && !!titleError}>
              <FieldLabel htmlFor="book-title">Titel</FieldLabel>
              <Input
                id="book-title"
                value={title}
                placeholder="Kofi und das Brüllen der Savanne"
                aria-invalid={touched.title && !!titleError}
                onChange={(e) => setTitle(e.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, title: true }))}
                autoFocus
              />
              {touched.title && titleError && (
                <FieldError>{titleError}</FieldError>
              )}
            </Field>
            <Field data-invalid={touched.slug && !!slugError}>
              <FieldLabel htmlFor="book-slug">Slug</FieldLabel>
              <Input
                id="book-slug"
                value={slug}
                placeholder="kofi-und-das-bruellen"
                className="font-mono"
                aria-invalid={touched.slug && !!slugError}
                onChange={(e) => setSlug(e.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, slug: true }))}
              />
              {touched.slug && slugError ? (
                <FieldError>{slugError}</FieldError>
              ) : (
                <FieldDescription>
                  Kann später nicht mehr geändert werden.
                </FieldDescription>
              )}
            </Field>
            <Field data-invalid={!!sceneError}>
              <FieldLabel htmlFor="book-scenes">Anzahl Szenen</FieldLabel>
              <Input
                id="book-scenes"
                type="number"
                min={0}
                className="w-28"
                value={Number.isNaN(sceneCount) ? "" : sceneCount}
                aria-invalid={!!sceneError}
                onChange={(e) => setSceneCount(e.target.valueAsNumber)}
              />
              {sceneError && <FieldError>{sceneError}</FieldError>}
            </Field>
            <Field>
              <FieldLabel htmlFor="book-handles">
                Shopify-Mapping (optional)
              </FieldLabel>
              <Textarea
                id="book-handles"
                rows={3}
                value={handles}
                placeholder={
                  "Kofi und das Brüllen der Savanne\nKOFI-HC\nKOFI-SC"
                }
                onChange={(e) => setHandles(e.target.value)}
              />
              <FieldDescription>
                Produkttitel, SKU oder Handle – einer pro Zeile. Ohne Mapping
                lassen sich Bestellungen nicht zuordnen.
              </FieldDescription>
            </Field>
          </FieldGroup>

          {err && <p className="text-destructive">{err}</p>}

          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={() => onOpenChange(false)}
            >
              Abbrechen
            </Button>
            <Button type="submit" disabled={busy}>
              {busy ? "Lege an…" : "Buch anlegen"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
